import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { Timer, CheckCircle, Target } from '@phosphor-icons/react';
import { FitBadge, ScoreBars } from '../../components/hikari/ScoreBreakdown';

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const AssessmentRun = () => {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const forJob = sp.get('for_job');
  const nav = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [composed, setComposed] = useState(null);  // { items, composition, time_limit_sec }
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => setAssessment(r.data));
  }, [id]);

  const start = async () => {
    const qs = forJob ? `?job_id=${encodeURIComponent(forJob)}` : '';
    const { data } = await api.post(`/assessments/${id}/start${qs}`);
    setAttemptId(data.attempt_id);
    setComposed(data);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  const setAnswer = (itemId, field, value) => {
    setAnswers((a) => ({ ...a, [itemId]: { ...(a[itemId] || {}), item_id: itemId, [field]: value } }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      const payload = {
        answers: Object.values(answers),
        duration_sec: elapsed,
      };
      const { data } = await api.post(`/assessments/attempts/${attemptId}/submit`, payload);
      setResult(data);
      toast.success('Attempt submitted');
    } catch (e) {
      toast.error('Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!assessment) return <Shell><div className="p-8 mono text-sm">Loading...</div></Shell>;

  // RESULT STATE
  if (result) {
    return (
      <Shell>
        <div className="max-w-[900px] mx-auto px-6 py-14" data-testid="attempt-result">
          <CheckCircle size={48} weight="fill" className="text-primary mb-4" />
          <div className="label-eyebrow text-primary mb-2">ATTEMPT COMPLETE</div>
          <h1 className="font-display font-black text-5xl tracking-tighter mb-8">Your raw score</h1>

          <div className="border border-border bg-white p-10 mb-6 flex items-center gap-8">
            <FitBadge value={result.raw_score} size="lg" />
            <div>
              <div className="label-eyebrow text-muted-foreground">ITEMS ANSWERED</div>
              <div className="mono font-bold text-2xl">{result.sub_scores.item_count}</div>
              <div className="label-eyebrow text-muted-foreground mt-3">VARIANCE (WITHIN ATTEMPT)</div>
              <div className="mono font-bold text-2xl">{result.sub_scores.variance}</div>
            </div>
          </div>

          {result.flagged_signals.length > 0 && (
            <div className="border border-border bg-accent/20 p-6 mb-6">
              <div className="label-eyebrow text-accent-foreground mb-2">RELIABILITY FLAGS</div>
              <ul className="text-sm">
                {result.flagged_signals.map((f) => <li key={f}>· {f.replaceAll('_', ' ')}</li>)}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={() => nav('/candidate')} className="rounded-none" data-testid="attempt-done">Back to dashboard</Button>
            <Button variant="outline" onClick={() => nav('/candidate/assessments')} className="rounded-none" data-testid="attempt-more">Take another</Button>
          </div>
        </div>
      </Shell>
    );
  }

  // PRE-START STATE
  if (!attemptId) {
    return (
      <Shell>
        <div className="max-w-[800px] mx-auto px-6 py-14">
          <div className="label-eyebrow text-primary mb-2">ASSESSMENT</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">{assessment.title}</h1>
          <div className="mono text-sm text-muted-foreground mb-8">
            {Math.round(assessment.time_limit_sec / 60)} min recommended · Type: {assessment.type}
          </div>
          {forJob && (
            <div className="border border-primary bg-primary/5 p-5 mb-6 flex items-start gap-3" data-testid="for-job-banner">
              <Target size={20} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <div className="label-eyebrow text-primary mb-1">CUSTOMIZED FOR THIS JOB</div>
                <p className="text-sm leading-relaxed">
                  Your questions will be picked from the bank based on what this specific role actually requires —
                  you'll get the common core plus items weighted toward the job's required skills.
                </p>
              </div>
            </div>
          )}
          <div className="border border-border bg-white p-6 mb-6">
            <p className="text-sm leading-relaxed">
              Answer each item carefully. {assessment.type === 'mcq' ? 'Pick the best single option.' : 'Write clearly — your answer will be graded by an AI rubric against job-relevant criteria.'}
              &nbsp;Consistency across multiple attempts is a factor in your overall fit score.
            </p>
          </div>
          <Button onClick={start} className="rounded-none h-12 px-8" data-testid="start-attempt">Begin attempt</Button>
        </div>
      </Shell>
    );
  }

  // IN-PROGRESS STATE — use composed items from the start response
  const items = composed?.items || [];
  const progress = items.length > 0 ? (Object.keys(answers).length / items.length) * 100 : 0;

  return (
    <Shell>
      <div className="max-w-[900px] mx-auto px-6 py-10" data-testid="attempt-runner">
        {/* Header with timer */}
        <div className="sticky top-16 bg-white/90 backdrop-blur z-20 border-b border-border -mx-6 px-6 py-3 mb-8 flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-muted-foreground">ATTEMPT IN PROGRESS</div>
            <div className="font-display font-black text-lg tracking-tight">{assessment.title}</div>
            {composed?.composition?.job_title && (
              <div className="mono text-xs text-primary mt-0.5">FOR: {composed.composition.job_title}</div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 mono">
              <Timer size={18} className="text-primary" />
              <span className="font-bold text-lg">{fmt(elapsed)}</span>
            </div>
            <div className="w-40">
              <Progress value={progress} className="h-2 rounded-none" />
              <div className="mono text-xs mt-1 text-right">{Object.keys(answers).length}/{items.length}</div>
            </div>
          </div>
        </div>

        {composed?.composition && (
          <div className="mb-6 border border-border bg-secondary p-4 mono text-xs" data-testid="composition-info">
            COMPOSITION · {composed.composition.core_count} core + {composed.composition.pool_count} skill-tagged
            {composed.composition.job_required_skills?.length > 0 && (
              <> · weighted toward: {composed.composition.job_required_skills.join(', ')}</>
            )}
          </div>
        )}

        <div className="space-y-6">
          {items.map((it, idx) => (
            <div key={it.id} className="border border-border bg-white p-6" data-testid={`item-${it.id}`}>
              <div className="label-eyebrow text-primary mb-2">QUESTION {idx + 1}</div>
              <div className="font-medium text-base mb-4 leading-relaxed">{it.prompt}</div>

              {(assessment.type === 'mcq' || assessment.type === 'simulation') && it.options && (
                <RadioGroup
                  value={String(answers[it.id]?.selected_index ?? '')}
                  onValueChange={(v) => setAnswer(it.id, 'selected_index', Number(v))}
                  className="space-y-2"
                >
                  {it.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-3 border border-border p-3 cursor-pointer hover:bg-secondary/40" onClick={() => setAnswer(it.id, 'selected_index', i)}>
                      <RadioGroupItem value={String(i)} id={`${it.id}-${i}`} data-testid={`opt-${it.id}-${i}`} />
                      <Label htmlFor={`${it.id}-${i}`} className="flex-1 cursor-pointer text-sm">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {(assessment.type === 'writing' || assessment.type === 'scenario' || assessment.type === 'timed_task') && (
                <div>
                  {assessment.type === 'timed_task' && (
                    <div className="mb-3 p-2 bg-accent/20 text-xs label-eyebrow">
                      TIMED TASK · Finishing on time is part of the signal.
                    </div>
                  )}
                  <Textarea
                    value={answers[it.id]?.text || ''}
                    onChange={(e) => setAnswer(it.id, 'text', e.target.value)}
                    rows={8}
                    className="rounded-none"
                    placeholder="Type your response..."
                    data-testid={`answer-${it.id}`}
                  />
                  <div className="flex items-center justify-between mt-2 mono text-xs text-muted-foreground">
                    <span>Words: {(answers[it.id]?.text || '').split(/\s+/).filter(Boolean).length}</span>
                    {it.min_words && <span>Minimum: {it.min_words}</span>}
                  </div>
                  {it.rubric && (
                    <div className="mt-3 p-3 bg-secondary/60 text-xs label-eyebrow text-muted-foreground">
                      RUBRIC: <span className="normal-case tracking-normal font-normal">{it.rubric}</span>
                    </div>
                  )}
                </div>
              )}

              {assessment.type === 'work_style' && (
                <div>
                  <div className="label-eyebrow text-muted-foreground mb-3 text-xs">STRONGLY DISAGREE ←→ STRONGLY AGREE</div>
                  <div className="grid grid-cols-5 gap-0 grid-skeleton" data-testid={`likert-${it.id}`}>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAnswer(it.id, 'selected_index', v - 1)}
                        className={`py-4 text-center transition-colors ${answers[it.id]?.selected_index === v - 1 ? 'bg-foreground text-background' : 'bg-white hover:bg-secondary/50'}`}
                        data-testid={`likert-${it.id}-${v}`}
                      >
                        <div className="mono font-black text-2xl">{v}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-3 items-center">
          <Button onClick={submit} disabled={submitting || Object.keys(answers).length === 0} className="rounded-none h-12 px-8" data-testid="submit-attempt">
            {submitting ? 'Grading...' : 'Submit attempt'}
          </Button>
          <span className="text-xs text-muted-foreground mono">
            Writing and scenario items are graded by Claude Sonnet 4.5 against the rubric.
          </span>
        </div>
      </div>
    </Shell>
  );
};

export default AssessmentRun;
